import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Ruler, CalendarCheck } from "lucide-react";
import { useLang } from "@/lib/i18n";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onNavigate }) {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative grain overflow-hidden bg-[#0E1A2B] pt-32 pb-20 sm:pt-40 sm:pb-28"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute -top-32 -right-32 h-[34rem] w-[34rem] rounded-full bg-[#F26522]/15 blur-3xl" />
        <div className="absolute bottom-0 -left-40 h-[28rem] w-[28rem] rounded-full bg-[#162B46] blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />
      </motion.div>

      <div className="relative px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-[#F26522] font-semibold mb-6"
            data-testid="hero-eyebrow"
          >
            {t("hero_eyebrow")}
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-white">
            <MaskedLine delay={0.15}>{t("hero_l1")}</MaskedLine>
            <MaskedLine delay={0.3} className="text-[#F26522]">
              {t("hero_l2")}
            </MaskedLine>
            <MaskedLine delay={0.45}>{t("hero_l3")}</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
            data-testid="hero-subcopy"
          >
            {t("hero_sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => onNavigate("quote")}
              className="inline-flex items-center gap-2 rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold px-7 py-4 text-base transition-[background-color,transform] duration-200 hover:scale-[1.04] active:scale-95 shadow-[0_10px_40px_-10px_rgba(242,101,34,0.7)]"
              data-testid="hero-quote-btn"
            >
              <Ruler className="h-5 w-5" />
              {t("hero_cta1")}
            </button>
            <button
              onClick={() => onNavigate("book")}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-[#F26522] text-white hover:text-[#F26522] font-display font-bold px-7 py-4 text-base transition-[border-color,color,transform] duration-200 hover:scale-[1.04] active:scale-95"
              data-testid="hero-book-btn"
            >
              <CalendarCheck className="h-5 w-5" />
              {t("hero_cta2")}
            </button>
            <a
              href="tel:3608617655"
              className="inline-flex items-center gap-2 py-2 text-slate-300 hover:text-white font-semibold transition-colors duration-200"
              data-testid="hero-phone-link"
            >
              <Phone className="h-4 w-4 text-[#F26522]" />
              360-861-7655
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-10 flex flex-wrap gap-2"
          >
            {[t("chip1"), t("chip2"), t("chip3"), t("chip4")].map((chip) => (
              <span
                key={chip}
                className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-300"
              >
                {chip}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y: logoY }}
            initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto max-w-sm"
          >
            <div className="absolute -inset-8 rounded-full bg-[#F26522]/25 blur-3xl" />
            <div className="relative rounded-[2rem] bg-white p-6 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/20 rotate-2">
              <img
                src="/logo.png"
                alt="PERETE Flooring and Carpet Installation Inc. badge logo with red carpet roll and wood planks"
                className="w-full h-auto rounded-2xl"
                data-testid="hero-logo-img"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="absolute -bottom-6 -left-6 rounded-2xl bg-[#F26522] text-white px-5 py-4 shadow-xl"
              data-testid="hero-badge-years"
            >
              <p className="font-display font-extrabold text-2xl leading-none">
                {t("badge_free")}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] mt-1">
                {t("badge_free_sub")}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
